<template>
  <b-modal
    ref="DepositTransactionHistoryModal"
    id="DepositTransactionHistoryModal"
    size="lg"
    title="ประวัติเงินมัดจำ"
    hide-footer
  >
    <!-- Balance Summary -->
    <b-card bg-variant="success" text-variant="white" class="mb-3">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <small>ยอดเงินมัดจำคงเหลือ</small>
          <h4 class="mb-0">{{ formatCurrency(depositBalance) }} บาท</h4>
        </div>
      </div>
    </b-card>

    <!-- Transaction History Table -->
    <b-card no-body>
      <b-table
        small
        hover
        head-variant="light"
        :busy="isLoading"
        :items="transactionHistory"
        :fields="tableFields"
        show-empty
        responsive
      >
        <template #cell(creationDt)="row">
          {{ formatDate(row.value) }}
        </template>

        <template #cell(type)="row">
          <b-badge :variant="getTypeVariant(row.value)">
            {{ getTypeLabel(row.value) }}
          </b-badge>
        </template>

        <template #cell(amount)="row">
          <span :class="getAmountClass(row.item.type)">
            {{ getAmountPrefix(row.item.type) }}{{ formatCurrency(row.value) }}
          </span>
        </template>

        <template #cell(balanceAfter)="row">
          {{ formatCurrency(row.value) }}
        </template>

        <template #cell(receipt)="row">
          <span v-if="row.item.receipt">
            {{ row.item.receipt.receiptNo }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>

        <template #cell(branch)="row">
          <span v-if="row.item.branch">
            {{ row.item.branch.name }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>

        <template #empty>
          <p class="text-center text-muted my-2">ไม่มีประวัติเงินมัดจำ</p>
        </template>

        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>
      </b-table>
    </b-card>

    <!-- Pagination -->
    <div class="d-flex justify-content-between align-items-center mt-3" v-if="transactionTotal > perPage">
      <small class="text-muted">
        แสดง {{ currentOffset + 1 }} - {{ Math.min(currentOffset + perPage, transactionTotal) }}
        จาก {{ transactionTotal }} รายการ
      </small>
      <b-pagination
        v-model="currentPage"
        :total-rows="transactionTotal"
        :per-page="perPage"
        size="sm"
        @change="onPageChange"
      ></b-pagination>
    </div>
  </b-modal>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";
export default {
  name: "DepositTransactionHistory",
  props: {
    patientId: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      currentPage: 1,
      perPage: 20,
      tableFields: [
        {
          key: "creationDt",
          label: "วันที่",
          sortable: true
        },
        {
          key: "type",
          label: "ประเภท",
          class: "text-center"
        },
        {
          key: "amount",
          label: "จำนวน",
          class: "text-right",
          formatter: this.$utils.formatPrice,
        },
        {
          key: "balanceAfter",
          label: "ยอดคงเหลือ",
          class: "text-right"
        },
        {
          key: "receipt",
          label: "เลขที่ใบเสร็จ"
        },
        {
          key: "branch",
          label: "สาขา"
        },
        {
          key: "note",
          label: "หมายเหตุ"
        }
      ],
      localPatientId: null
    };
  },
  computed: {
    ...mapGetters({
      depositBalance: "moduleDeposit/getDepositBalance",
      transactionHistory: "moduleDeposit/getTransactionHistory",
      transactionTotal: "moduleDeposit/getTransactionTotal",
      isLoading: "moduleDeposit/isLoading"
    }),
    currentOffset() {
      return (this.currentPage - 1) * this.perPage;
    }
  },
  methods: {
    ...mapActions({
      fetchTransactionHistory: "moduleDeposit/fetchTransactionHistory"
    }),
    show(patientId) {
      this.localPatientId = patientId || this.patientId;
      this.currentPage = 1;
      this.$refs.DepositTransactionHistoryModal.show();
      this.loadTransactions();
    },
    loadTransactions() {
      if (!this.localPatientId) return;

      this.fetchTransactionHistory({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
        patientId: this.localPatientId,
        limit: this.perPage,
        offset: this.currentOffset
      });
    },
    onPageChange(page) {
      this.currentPage = page;
      this.loadTransactions();
    },
    formatDate(date) {
      if (date) {
        return moment(date).locale("th").format("D MMM YY HH:mm");
      }
      return "-";
    },
    formatCurrency(value) {
      return this.$utils.formatCurrency(value);
    },
    getTypeVariant(type) {
      const variants = {
        DEPOSIT: "success",
        DEDUCTION: "danger",
        CANCEL_DEPOSIT: "warning",
        CANCEL_DEDUCTION: "info",
        ADJUSTMENT: "secondary"
      };
      return variants[type] || "secondary";
    },
    getTypeLabel(type) {
      const labels = {
        DEPOSIT: "รับฝาก",
        DEDUCTION: "ตัดใช้",
        CANCEL_DEPOSIT: "ยกเลิกรับฝาก",
        CANCEL_DEDUCTION: "ยกเลิกการใช้",
        ADJUSTMENT: "ปรับปรุง"
      };
      return labels[type] || type;
    },
    getAmountClass(type) {
      if (type === "DEPOSIT" || type === "CANCEL_DEDUCTION") {
        return "text-success font-weight-bold";
      }
      return "text-danger font-weight-bold";
    },
    getAmountPrefix(type) {
      if (type === "DEPOSIT" || type === "CANCEL_DEDUCTION") {
        return "+";
      }
      return "-";
    }
  }
};
</script>
